import { c as _c } from "react/compiler-runtime";
import type { HofDashboardEntry } from '@/features/hof-dashboard/api/hof-dashboard-data';
import { LABEL_CLASS } from '@/features/hof-dashboard/components/dashboard-ui';
import { formatDateTime, translate } from '@/features/hof-dashboard/texts';
import { entrySteps, type TimelineStep } from '@/features/hof-dashboard/utils/review-steps';
import type { Locale } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import type React from 'react';
const STEP_DOT: Record<TimelineStep['tone'], string> = {
  version: 'bg-gray-300',
  review: 'bg-gray-500',
  revision: 'bg-amber-600',
  accepted: 'bg-conveniat-green'
};

/** Steps of a history on a rail, each with who did it and when, and its comment quoted. */
export const Timeline: React.FC<{
  steps: readonly TimelineStep[];
  locale: Locale;
}> = t0 => {
  const $ = _c(8);
  const {
    steps,
    locale
  } = t0;
  let t1;
  if ($[0] !== locale || $[1] !== steps) {
    let t2;
    if ($[3] !== locale || $[4] !== steps.length) {
      t2 = (step, index) => <li key={`${step.at}-${step.title}`} className="relative flex gap-3 pb-5 last:pb-0">{index < steps.length - 1 && <span aria-hidden={true} className="bg-rail absolute top-4 bottom-0 left-[5px] w-px" />}<span aria-hidden={true} className={cn("relative mt-1.5 h-3 w-3 shrink-0 rounded-full", STEP_DOT[step.tone])} /><div className="min-w-0 space-y-1"><p className="text-base font-semibold text-green-900">{step.title}</p><p className="text-xs text-green-700">{formatDateTime(step.at, locale)} · {step.by}</p>{step.lines.map(_temp)}{step.quote !== undefined && <p className="bg-quiet rounded-lg px-3 py-2 text-sm whitespace-pre-line text-green-800">«{step.quote}»</p>}</div></li>;
      $[3] = locale;
      $[4] = steps.length;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    t1 = steps.map(t2);
    $[0] = locale;
    $[1] = steps;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[6] !== t1) {
    t2 = <ol>{t1}</ol>;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};

/**
 * The history of one submission, open for the Hof and the Ressort alike: who handed it in, and
 * every status the Ressort set and every comment it wrote, each with the name and the time.
 */
export const EntryTimeline: React.FC<{
  entry: HofDashboardEntry;
  locale: Locale;
}> = t0 => {
  const $ = _c(13);
  const {
    entry,
    locale
  } = t0;
  let t1;
  if ($[0] !== locale) {
    t1 = translate("history", locale);
    $[0] = locale;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <h5 className={LABEL_CLASS}>{t1}</h5>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== entry || $[5] !== locale) {
    t3 = entrySteps(entry, locale);
    $[4] = entry;
    $[5] = locale;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== locale || $[8] !== t3) {
    t4 = <Timeline steps={t3} locale={locale} />;
    $[7] = locale;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t4) {
    t5 = <section className="space-y-3">{t2}{t4}</section>;
    $[10] = t2;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};
function _temp(line) {
  return <p key={line} className="text-sm text-green-800">{line}</p>;
}